// Mock data for Freezer/Cooler units based on real locations in Cary
// Temperature patterns are determined by unit type, location, and environmental factors

// Location types and their typical operating conditions
const LOCATION_TYPES = {
  ACADEMY_PAVILION: { // Event space - variable load based on events
    baseLoad: 0.6,
    peakHours: [10, 11, 12, 18, 19, 20], // Event and meeting times
    weekendMultiplier: 1.8,
    ambientTempVariation: 3, // °C variation due to foot traffic
    powerStability: 0.95 // 95% power stability
  },
  BARK_BAR: { // Dog park area - high usage during peak times
    baseLoad: 0.8,
    peakHours: [7, 8, 17, 18, 19], // Dog walking times
    weekendMultiplier: 2.2,
    ambientTempVariation: 4, // Higher variation due to outdoor proximity
    powerStability: 0.92
  }
};

// Unit types and their specifications
const UNIT_TYPES = {
  FREEZER: {
    targetTemp: -18, // °C
    targetRange: { min: -20, max: -15 },
    criticalHigh: -10,
    criticalLow: -25,
    powerConsumption: 150, // watts
    defrostCycle: 6 // hours between defrost cycles
  },
  BEER_COOLER: {
    targetTemp: 2, // °C
    targetRange: { min: 0, max: 4 },
    criticalHigh: 8,
    criticalLow: -2,
    powerConsumption: 120, // watts
    defrostCycle: 12 // hours between defrost cycles
  },
  COOLER: {
    targetTemp: 4, // °C
    targetRange: { min: 2, max: 6 },
    criticalHigh: 10,
    criticalLow: 0,
    powerConsumption: 100, // watts
    defrostCycle: 24 // hours between defrost cycles
  }
};

// Helper function to determine location type from unit name
const getLocationType = (unitName) => {
  const name = unitName.toLowerCase();
  if (name.includes('academy pavilion')) return LOCATION_TYPES.ACADEMY_PAVILION;
  if (name.includes('bark bar')) return LOCATION_TYPES.BARK_BAR;
  return LOCATION_TYPES.ACADEMY_PAVILION; // Default
};

// Helper function to determine unit type from name
const getUnitType = (unitName) => {
  const name = unitName.toLowerCase();
  if (name.includes('freezer')) return UNIT_TYPES.FREEZER;
  if (name.includes('beer cooler')) return UNIT_TYPES.BEER_COOLER;
  if (name.includes('cooler')) return UNIT_TYPES.COOLER;
  return UNIT_TYPES.COOLER; // Default
};

// Helper function to calculate temperature and performance metrics
const calculateTemperatureMetrics = (unitType, locationType, dayOffset = 0, hourOffset = 0, isWeekend = false, seed = 0) => {
  const currentHour = (new Date().getHours() + hourOffset) % 24;
  
  let loadFactor = locationType.baseLoad;
  
  // Add peak hour impact
  if (locationType.peakHours.includes(currentHour)) {
    loadFactor += 0.3;
  }
  
  // Weekend multiplier
  if (isWeekend) {
    loadFactor *= locationType.weekendMultiplier;
  }
  
  // Add deterministic "randomness" based on seed
  const pseudoRandom1 = (Math.sin(seed * 1234) + 1) / 2;
  const pseudoRandom2 = (Math.sin(seed * 5678) + 1) / 2;
  const pseudoRandom3 = (Math.sin(seed * 9012) + 1) / 2;
  const pseudoRandom4 = (Math.sin(seed * 3456) + 1) / 2;
  
  loadFactor += (pseudoRandom1 - 0.5) * 0.4; // Add variation
  loadFactor = Math.max(0.1, Math.min(1.5, loadFactor));
  
  // Calculate temperature deviation from target
  const baseTemp = unitType.targetTemp;
  let tempDeviation = 0;
  
  // Load-based temperature variation
  tempDeviation += loadFactor * 2; // Higher load = warmer temp
  
  // Ambient temperature variation
  tempDeviation += (pseudoRandom2 - 0.5) * locationType.ambientTempVariation;
  
  // Power stability issues
  if (pseudoRandom3 > locationType.powerStability) {
    tempDeviation += (pseudoRandom4 - 0.5) * 5; // Power fluctuation
  }
  
  const currentTemp = baseTemp + tempDeviation;
  
  // Determine status based on temperature
  let status = 'OK';
  let health = 'Excellent';
  
  if (currentTemp > unitType.criticalHigh || currentTemp < unitType.criticalLow) {
    status = 'Critical';
    health = 'Poor';
  } else if (currentTemp > unitType.targetRange.max || currentTemp < unitType.targetRange.min) {
    status = 'Warning';
    health = currentTemp > unitType.targetRange.max + 2 || currentTemp < unitType.targetRange.min - 2 ? 'Fair' : 'Good';
  }
  
  // Simulate occasional malfunctions
  if (pseudoRandom1 < 0.03) { // 3% chance of malfunction
    status = 'Critical';
    health = 'Poor';
    tempDeviation += (pseudoRandom2 - 0.5) * 10;
  }
  
  // Calculate energy efficiency
  const expectedPower = unitType.powerConsumption;
  const actualPower = expectedPower * (1 + loadFactor * 0.3 + (pseudoRandom3 - 0.5) * 0.2);
  const efficiency = Math.round((expectedPower / actualPower) * 100);
  
  return {
    currentTemp: Math.round(currentTemp * 10) / 10,
    status,
    health,
    loadFactor,
    powerConsumption: Math.round(actualPower),
    efficiency: Math.max(60, Math.min(100, efficiency)),
    tempDeviation: Math.round(tempDeviation * 10) / 10
  };
};

// Helper function to generate time series temperature data
const generateTemperatureTimeSeries = (unitType, locationType, seed = 0) => {
  const timeSeries = [];
  const now = new Date();
  
  // Generate 7 days of temperature data
  for (let i = 6; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    
    // Generate multiple readings per day for more realistic data
    const dailyReadings = [];
    for (let hour = 0; hour < 24; hour += 4) { // Every 4 hours
      const metrics = calculateTemperatureMetrics(unitType, locationType, -i, hour, isWeekend, seed + i + hour);
      dailyReadings.push(metrics.currentTemp);
    }
    
    // Calculate daily statistics
    const avgTemp = dailyReadings.reduce((sum, temp) => sum + temp, 0) / dailyReadings.length;
    const minTemp = Math.min(...dailyReadings);
    const maxTemp = Math.max(...dailyReadings);
    
    timeSeries.push({
      day: i === 0 ? 'Today' : `Day -${i}`,
      avg: Math.round(avgTemp * 10) / 10,
      min: Math.round(minTemp * 10) / 10,
      max: Math.round(maxTemp * 10) / 10,
      timestamp: date.toISOString()
    });
  }
  
  return timeSeries;
};

// Generate mock data for all freezer/cooler units
export const generateFreezerMockData = () => {
  const unitDefinitions = [
    { name: "Academy Pavilion Beer Cooler", mac: "b2:f2:5e:c7:58:48", lat: 35.7924184, lng: -78.7807368 },
    { name: "Academy Pavilion Freezer", mac: "42:41:55:c3:67:b4", lat: 35.7923932, lng: -78.7801632 },
    { name: "Bark Bar Cooler", mac: "16:77:e9:74:05:c0", lat: 35.7920385, lng: -78.781342 }
  ];

  const units = unitDefinitions.map((unitDef, index) => {
    const locationType = getLocationType(unitDef.name);
    const unitType = getUnitType(unitDef.name);
    const seed = index * 987; // Use index as seed for consistent data
    const currentMetrics = calculateTemperatureMetrics(unitType, locationType, 0, 0, false, seed);
    
    // Generate additional deterministic values
    const pseudoRandom1 = (Math.sin(seed * 1111) + 1) / 2;
    const pseudoRandom2 = (Math.sin(seed * 2222) + 1) / 2;
    const pseudoRandom3 = (Math.sin(seed * 3333) + 1) / 2;
    const pseudoRandom4 = (Math.sin(seed * 4444) + 1) / 2;
    
    // Calculate maintenance schedule
    const daysSinceLastMaintenance = Math.floor(pseudoRandom1 * 30) + 1; // 1-31 days ago
    const lastMaintenanceAt = new Date(Date.now() - daysSinceLastMaintenance * 24 * 60 * 60 * 1000);
    
    // Next maintenance based on unit type and current health
    const maintenanceInterval = unitType === UNIT_TYPES.FREEZER ? 90 : 120; // days
    const daysUntilMaintenance = currentMetrics.health === 'Poor' ? 
      Math.floor(pseudoRandom2 * 7) + 1 : // 1-7 days if poor health
      Math.floor(pseudoRandom2 * maintenanceInterval) + 30; // 30-150 days if good
    const nextMaintenanceAt = new Date(Date.now() + daysUntilMaintenance * 24 * 60 * 60 * 1000);
    
    // Determine unit category
    const category = unitDef.name.toLowerCase().includes('freezer') ? 'Freezer' :
                    unitDef.name.toLowerCase().includes('beer') ? 'Beer Cooler' : 'Cooler';
    
    // Calculate door openings (affects temperature stability)
    const dailyOpenings = Math.round(locationType.baseLoad * 50 + (pseudoRandom3 - 0.5) * 20); // 20-80 openings per day
    
    return {
      id: `FREEZER-${String(index + 1).padStart(3, '0')}`,
      name: unitDef.name,
      location: unitDef.name.replace(/ (Freezer|Cooler|Beer Cooler)$/, ''), // Remove unit type from location
      macAddress: unitDef.mac,
      coordinates: {
        latitude: unitDef.lat,
        longitude: unitDef.lng
      },
      category: category,
      status: currentMetrics.status,
      health: currentMetrics.health,
      currentTempC: currentMetrics.currentTemp,
      targetTempC: unitType.targetTemp,
      targetRangeC: unitType.targetRange,
      lastMaintenanceAt: lastMaintenanceAt.toISOString(),
      nextMaintenanceAt: nextMaintenanceAt.toISOString(),
      locationType: Object.keys(LOCATION_TYPES).find(key => LOCATION_TYPES[key] === locationType),
      temp7d: generateTemperatureTimeSeries(unitType, locationType, seed),
      // Performance metrics
      performance: {
        powerConsumption: currentMetrics.powerConsumption,
        efficiency: currentMetrics.efficiency,
        dailyOpenings: dailyOpenings,
        avgOpenDuration: Math.round(pseudoRandom1 * 30) + 15, // 15-45 seconds
        defrostCycle: unitType.defrostCycle,
        lastDefrost: new Date(Date.now() - Math.floor(pseudoRandom2 * unitType.defrostCycle) * 60 * 60 * 1000).toISOString(),
        compressorRuntime: Math.round(pseudoRandom3 * 40) + 40, // 40-80% runtime
        fanSpeed: Math.round(pseudoRandom4 * 30) + 70 // 70-100% fan speed
      },
      // Sensor and maintenance data
      sensors: {
        temperatureSensor: pseudoRandom1 > 0.05 ? 'Online' : 'Offline',
        doorSensor: pseudoRandom2 > 0.03 ? 'Online' : 'Offline',
        compressorSensor: pseudoRandom3 > 0.02 ? 'Online' : 'Offline',
        batteryLevel: Math.floor(pseudoRandom4 * 30) + 70, // 70-100%
        lastCalibration: new Date(Date.now() - Math.floor(pseudoRandom1 * 60) * 24 * 60 * 60 * 1000).toISOString(),
        signalStrength: Math.floor(pseudoRandom2 * 30) + 70 // 70-100%
      },
      // Alerts and issues
      alerts: {
        temperatureAlerts: currentMetrics.status === 'Critical' ? ['Temperature out of safe range'] : 
                          currentMetrics.status === 'Warning' ? ['Temperature approaching limits'] : [],
        maintenanceAlerts: currentMetrics.health === 'Poor' ? ['Unit requires immediate maintenance'] :
                          daysUntilMaintenance < 7 ? ['Maintenance due soon'] : [],
        sensorAlerts: [
          ...(pseudoRandom1 <= 0.05 ? ['Temperature sensor offline'] : []),
          ...(pseudoRandom2 <= 0.03 ? ['Door sensor offline'] : []),
          ...(pseudoRandom3 <= 0.02 ? ['Compressor sensor offline'] : [])
        ]
      }
    };
  });

  return {
    units: units,
    summary: {
      totalUnits: units.length,
      criticalUnits: units.filter(u => u.status === 'Critical').length,
      warningUnits: units.filter(u => u.status === 'Warning').length,
      okUnits: units.filter(u => u.status === 'OK').length,
      averageTemp: Math.round(units.reduce((sum, u) => sum + u.currentTempC, 0) / units.length * 10) / 10,
      totalPowerConsumption: units.reduce((sum, u) => sum + u.performance.powerConsumption, 0),
      averageEfficiency: Math.round(units.reduce((sum, u) => sum + u.performance.efficiency, 0) / units.length),
      unitsNeedingMaintenance: units.filter(u => u.health === 'Poor' || u.alerts.maintenanceAlerts.length > 0).length,
      offlineSensors: units.reduce((sum, u) => sum + u.alerts.sensorAlerts.length, 0),
      lastUpdated: new Date().toISOString()
    }
  };
};

// Get unit data by name or MAC address
export const getUnitByName = (unitName) => {
  const allData = generateFreezerMockData();
  return allData.units.find(unit => 
    unit.name === unitName || unit.macAddress === unitName
  );
};

// Get units by category
export const getUnitsByCategory = (category) => {
  const allData = generateFreezerMockData();
  return allData.units.filter(unit => unit.category === category);
};

// Get units needing attention
export const getUnitsNeedingAttention = () => {
  const allData = generateFreezerMockData();
  return allData.units.filter(unit => 
    unit.status === 'Critical' ||
    unit.status === 'Warning' ||
    unit.health === 'Poor' ||
    unit.alerts.sensorAlerts.length > 0
  );
};

// Export default data for immediate use
export default generateFreezerMockData();
